// Sugalarm — notification-settings bridge wrapper (cycle 226).
//
// The single owner of access to the native `NotificationSettings` plugin.
// That plugin exists ONLY in the iOS app (the iOS Capacitor target); it is
// absent on web, the Pi kiosk, the Android tablet kiosk, and Android phones
// — so `isAvailable()` (and the `isIosNative()` gate) is false on every
// surface except the iOS shell, and the Settings → Notifications Critical
// Alerts + Hey Siri cards gate strictly on it.
//
// Like every native plugin in the pure-remote webview (cycle 26/138/197), it
// is reached through the bridge-injected `window.Capacitor.Plugins` proxy —
// NEVER a bare `import("@capacitor/...")`, which has no bundler to resolve
// against at runtime. Mirrors the `capacitorPlugin<T>()` helper in
// `lib/capacitor.ts` and the `lib/screenBrightness.ts` pattern.
//
// Read-only / open-a-system-surface only: it sends NO data anywhere, reads
// NO PHI, holds NO secret, and makes NO network call. It reads the local OS
// notification settings and opens system UI.

import { isCapacitorNative, capacitorPlatform } from "./capacitor";

/** OS authorization status for this app's notifications. */
export type NotificationAuthorization =
  | "authorized"
  | "denied"
  | "notDetermined"
  | "provisional"
  | "ephemeral";

/** Tri-state for an individual notification capability. */
export type NotificationCapability = "enabled" | "disabled" | "notSupported";

export interface NotificationStatus {
  authorization: NotificationAuthorization;
  criticalAlerts: NotificationCapability;
  sound: NotificationCapability;
  alert: NotificationCapability;
}

/** Cycle 365: what the GLANCE surfaces can actually do on this device.
 *  `widgetInstalled` is the honest answer to "does this household even
 *  have a widget on screen?", which nothing could answer before — a
 *  glance-only user with no widget installed and a throttled Live
 *  Activity has no working glance at all. */
export interface GlanceStatus {
  liveActivitiesEnabled: boolean;
  /** A Live Activity for this app is running right now. */
  activityRunning: boolean;
  /** At least one Sugalarm glucose widget is installed (any family). */
  widgetInstalled: boolean;
}

interface NotificationSettingsProxy {
  getStatus(): Promise<NotificationStatus>;
  getGlanceStatus(): Promise<GlanceStatus>;
  openAppNotificationSettings(): Promise<{ opened: boolean }>;
  openShortcutsApp(): Promise<{ opened: boolean }>;
}

/** True when the SPA is running inside the iOS native shell. The Critical
 *  Alerts + Hey Siri cards render only when this is true; off iOS-native
 *  (web, Android, Pi kiosk) this module is inert. Reuses the single-owner
 *  `isCapacitorNative()` detector plus the iOS platform check. */
export function isIosNative(): boolean {
  return isCapacitorNative() && capacitorPlatform() === "ios";
}

/** Resolve the natively-registered plugin proxy off the Capacitor bridge,
 *  or null on any surface where it isn't present (everything but iOS). */
function plugin(): NotificationSettingsProxy | null {
  if (!isIosNative()) return null;
  const cap = (
    window as { Capacitor?: { Plugins?: Record<string, unknown> } }
  ).Capacitor;
  const p = cap?.Plugins?.NotificationSettings;
  return p != null ? (p as NotificationSettingsProxy) : null;
}

/** True iff the native NotificationSettings plugin is registered on the
 *  bridge — i.e. the SPA is running inside the iOS app with this build's
 *  plugin compiled in. */
export function isAvailable(): boolean {
  return plugin() != null;
}

/** Read the current OS notification settings, or null off iOS-native / on
 *  any bridge error. Never throws. */
export async function getStatus(): Promise<NotificationStatus | null> {
  const p = plugin();
  if (p == null) return null;
  try {
    return await p.getStatus();
  } catch (err) {
    console.warn("[notificationSettings] getStatus failed", err);
    return null;
  }
}

/** Open this app's page in iOS Settings (the recovery path when Critical
 *  Alerts was denied — re-requesting in-app is a silent no-op once denied).
 *  No-ops off iOS-native. Never throws. */
export async function openAppNotificationSettings(): Promise<void> {
  const p = plugin();
  if (p == null) return;
  try {
    await p.openAppNotificationSettings();
  } catch (err) {
    console.warn("[notificationSettings] openAppNotificationSettings failed", err);
  }
}

/** Open the iOS Shortcuts app so the user can pin / customize the existing
 *  Siri phrases. No-ops off iOS-native. Never throws. */
export async function openShortcutsApp(): Promise<void> {
  const p = plugin();
  if (p == null) return;
  try {
    await p.openShortcutsApp();
  } catch (err) {
    console.warn("[notificationSettings] openShortcutsApp failed", err);
  }
}

/** Cycle 365: read the glance-surface state (Live Activities enabled, an
 *  activity actually running, a widget actually installed). Returns null
 *  off iOS-native, on any bridge error, AND on an older shell whose
 *  plugin predates this method — an old build must report "unknown"
 *  rather than a false `false`, which would show every pre-365 phone a
 *  bogus "no widget" nag. Never throws. */
export async function getGlanceStatus(): Promise<GlanceStatus | null> {
  const p = plugin();
  if (p == null) return null;
  if (typeof p.getGlanceStatus !== "function") return null;
  try {
    const s = await p.getGlanceStatus();
    if (s == null || typeof s.liveActivitiesEnabled !== "boolean") return null;
    return s;
  } catch (err) {
    console.warn("[notificationSettings] getGlanceStatus failed", err);
    return null;
  }
}
